/**
 * The Dev-only QA panel's queries and writes (L-36; plans/PKG-A.md A4). The
 * server answers /api/v1/qa/* only on test; the UI asks only when /healthz says
 * test, so production never sends these requests at all.
 */

import type {
  QaChecklistItem,
  QaFeedback,
  QaFeedbackCreate,
  QaFeedbackReviewRow,
  QaFeedbackWrite,
} from '@propos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { api } from '../../app/api.js';

export const QA_KEY = ['qa'] as const;

export function useQaChecklist(enabled: boolean): UseQueryResult<{ items: QaChecklistItem[] }> {
  return useQuery({
    queryKey: [...QA_KEY, 'checklist'],
    queryFn: () => api<{ items: QaChecklistItem[] }>('/api/v1/qa/checklist'),
    enabled,
  });
}

export function useMyQaFeedback(enabled: boolean): UseQueryResult<{ rows: QaFeedback[] }> {
  return useQuery({
    queryKey: [...QA_KEY, 'mine'],
    queryFn: () => api<{ rows: QaFeedback[] }>('/api/v1/qa/feedback/mine'),
    enabled,
  });
}

/** An answer: a new one is POSTed, a change to an existing one is PATCHed. */
export function useSaveQaFeedback(): UseMutationResult<
  unknown,
  Error,
  { create: QaFeedbackCreate } | { id: string; write: QaFeedbackWrite }
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      'create' in input
        ? api<{ id: string; version: number }>('/api/v1/qa/feedback', {
            method: 'POST',
            body: JSON.stringify(input.create),
          })
        : api<{ version: number }>(`/api/v1/qa/feedback/${input.id}`, {
            method: 'PATCH',
            body: JSON.stringify(input.write),
          }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: QA_KEY });
    },
  });
}

/** /admin/qa: everyone's feedback, under qa.review. */
export function useQaReview(
  query: string,
  enabled: boolean,
): UseQueryResult<{ rows: QaFeedbackReviewRow[] }> {
  return useQuery({
    queryKey: [...QA_KEY, 'review', query],
    queryFn: () => api<{ rows: QaFeedbackReviewRow[] }>(`/api/v1/qa/feedback?${query}`),
    placeholderData: (previous) => previous,
    enabled,
  });
}
